import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  ogType?: string;
  schemaData?: any;
}

export default function SEO({ 
  title, 
  description, 
  keywords, 
  canonical,
  ogType = 'website',
  schemaData 
}: SEOProps) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useEffect(() => {
    // 更新頁面標題
    document.title = title;
    
    // 更新或創建meta標籤
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // 更新link標籤
    const updateLinkTag = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // 基本SEO標籤
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);
    
    // Open Graph標籤
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:url', `${siteUrl}${canonical || window.location.pathname}`, true);
    updateMetaTag('og:site_name', '品牌入口建置服務', true);
    updateMetaTag('og:locale', 'zh_TW', true);
    
    // Twitter Card標籤
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    
    // Canonical URL
    if (canonical) {
      updateLinkTag('canonical', `${siteUrl}${canonical}`);
    }
    
    // Schema.org JSON-LD
    if (schemaData) {
      // 移除現有的Schema標籤
      const existingSchema = document.querySelector('script[type="application/ld+json"]');
      if (existingSchema) {
        existingSchema.remove();
      }
      
      // 添加新的Schema標籤
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schemaData);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, canonical, ogType, schemaData, siteUrl]);

  return null;
}